import React, { useState, useEffect, useRef, useContext } from 'react'
import { Row, Col, Card, Button, Form } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { FiCalendar } from 'react-icons/fi'
import Swal from 'sweetalert2'

import Select from 'components/Select'
import ProviderRoleTabs, { getProviderHexColor } from 'components/ProviderRoleTabs'
import AppointmentCalendarColorContext from './AppointmentCalendarColorContext'
import ExaminationSchedule from './ExaminationSchedule'
import AppointmentUiConfigModal from './AppointmentUiConfigModal'
import {
  APPOINTMENT_UI_CONFIG_CODE,
  APPOINTMENT_UI_CONFIG_KEY,
  APPOINTMENT_UI_CONFIG_URL,
  DEFAULT_APPOINTMENT_UI_CONFIG,
  getAppointmentUiConfigFromResponse,
  getConfigRecord,
  normalizeAppointmentUiConfig,
  parseJsonObject,
} from './appointmentUiConfig'

import httpClient from 'utility/httpClient.util'

const Calendar = ({ designerData = {}, designerKey = '', hn = '' }) => {
  const location = useLocation()
  const showProviderColor = useContext(AppointmentCalendarColorContext) ||
    [
      '/patient-file/opd/book-appointment/month',
      '/hospital/hosp-appointment-calendar',
      '/hospital/hosp-appointment-calendar/month',
    ].includes(location.pathname)
  const data = designerData[designerKey] || {}
  const appointmentHn = data?.hn || designerData?.hn || hn || ''
  const scheduleViewModeFromPath =
    location.pathname.split('/').filter(Boolean).pop() === 'month'
      ? 'month'
      : ''
  const [consultationOption, setConsultationOption] = useState([])
  const [options, setOptions] = useState({
    service: [],
    specialty: [],
    doctors: [],
    location: [],
  })
  const [config, setConfig] = useState()
  const [appointmentUiConfig, setAppointmentUiConfig] = useState(
    DEFAULT_APPOINTMENT_UI_CONFIG,
  )
  const [appointmentUiConfigDraft, setAppointmentUiConfigDraft] = useState(
    DEFAULT_APPOINTMENT_UI_CONFIG,
  )
  const [showAppointmentUiConfig, setShowAppointmentUiConfig] = useState(false)
  const [isLoadingAppointmentUiConfig, setIsLoadingAppointmentUiConfig] =
    useState(false)
  const [isSavingAppointmentUiConfig, setIsSavingAppointmentUiConfig] =
    useState(false)

  const searchParams = new URLSearchParams(location.search || '')
  const isDebugMode = ['true', '1'].includes(
    searchParams.get('isDebug') || searchParams.get('isDeub'),
  )

  const buttonOptions = [
    {
      variant: 'warning',
      color: '#FFCB87',
      label: 'อุดฟัน',
      value: 'อุดฟัน',
    },
    {
      variant: 'success',
      color: '#8ED4BC',
      label: 'ขูดหินปูน',
      value: 'ขูดหินปูน',
    },
    {
      variant: 'danger',
      color: '#FF9AA2',
      label: 'กรอฟัน',
      value: 'กรอฟัน',
    },
    {
      variant: 'primary',
      color: '#54B6E4',
      label: 'จัดฟัน',
      value: 'จัดฟัน',
    },

    {
      variant: 'info',
      color: '#99A9E8',
      label: 'ถอนฟัน',
      value: 'ถอนฟัน',
    },
  ]

  const initialValue = {
    specialty: { specialtyCode: '', name: '' },
    examRoom: { value: '', label: '' },
    doctor: { doctorId: '', fullName: '' },
    consultationType: { value: '', label: '' },
  }

  const IconButtons = consultationOption.map((button, idx) => (
    <Button
      className="btn-icon mx-1 border-none"
      key={idx}
      style={{ backgroundColor: button.hexColor, border: button.hexColor }}
    >
      {button.name}
    </Button>
  ))

  const [filter, setFilter] = useState()
  const [specialtyOptions, setSpecialtyOptions] = useState([])
  const [doctorOptions, setDoctorOptions] = useState([])
  const [calendarConfigTypeId, setCalendarConfigTypeId] = useState(1)
  const [providerRole, setProviderRole] = useState('DOCTOR')
  const [providerRoles, setProviderRoles] = useState([])
  const [providerLoading, setProviderLoading] = useState(false)
  const [providerError, setProviderError] = useState(false)
  const [providerRefresh, setProviderRefresh] = useState(0)
  const providerRequest = useRef(0)
  const specialtyRequest = useRef(0)

  useEffect(() => {
    if (appointmentUiConfig?.providerRole !== true) return
    let active = true
    const loadProviderRoles = async () => {
      setProviderLoading(true)
      setProviderError(false)
      try {
        const response = await httpClient.get('/hr/calendar-provider-role')
        if (active) setProviderRoles(response?.data?.data || [])
      } catch (error) {
        if (active) {
          setProviderRoles([])
          setProviderError(true)
        }
      } finally {
        if (active) setProviderLoading(false)
      }
    }
    loadProviderRoles()
    return () => { active = false }
  }, [appointmentUiConfig?.providerRole, providerRefresh])

  useEffect(() => {
    if (appointmentUiConfig?.providerRole !== true || providerRole === 'DOCTOR') return
    let active = true
    const request = ++providerRequest.current
    setProviderLoading(true)
    setProviderError(false)
    setOptions((previous) => ({ ...previous, doctors: [] }))
    httpClient.get(`/hr/employee?role=${encodeURIComponent(providerRole)}`)
      .then(({ data }) => {
        if (active && request === providerRequest.current) setOptions((previous) => ({ ...previous, doctors: data?.data || [] }))
      })
      .catch(() => {
        if (active && request === providerRequest.current) setProviderError(true)
      })
      .finally(() => {
        if (active && request === providerRequest.current) setProviderLoading(false)
      })
    return () => { active = false }
  }, [providerRole, appointmentUiConfig?.providerRole, providerRefresh])

  const selectProviderRole = (roleCode) => {
    if (roleCode === providerRole) return
    setProviderRole(roleCode)
    specialtyRequest.current += 1
    setFilter(undefined)
    setProviderLoading(true)
    setOptions((previous) => ({ ...previous, doctors: [], specialty: [] }))
    if (roleCode === 'DOCTOR') getOptions()
  }

  const handleChange = async (field, value) => {
    setFilter({ ...filter, [field]: value })

    if (field === 'doctor') {
      await loadSpecialtyApi(value?.employeeCode)
    }
  }

  const loadSpecialtyApi = async (employeeCode) => {
    const request = ++specialtyRequest.current
    let api = `/core/specialty`
    if (employeeCode) {
      api = `/core/specialty?medicCode=${employeeCode}&time=${new Date()}`
    }
    const specialtyReq = await httpClient.get(api).then(({ data }) => data.data)

    const specialtyOptions =
      specialtyReq?.map((item) => ({
        ...item,
        label: `${item.specialtyCode} - ${item.name}`,
      })) || []

    // setSpecialtyOptions(specialtyOptions)
    if (request === specialtyRequest.current) {
      setOptions((previous) => ({ ...previous, specialty: specialtyOptions }))
    }
  }

  const getOptions = async () => {
    const request = ++providerRequest.current
    setProviderError(false)
    try {
      const serviceReq = httpClient
        .get(`/hr/calendar-service`)
        .then(({ data }) => data.data)
      const specialtyReq = httpClient
        .get(`/core/specialty`)
        .then(({ data }) => data.data)
      const doctorReq = await httpClient
        .get(`/hr/employee/getDoctorIpd`)
        .then(({ data }) => data.data)
      const locationReq = httpClient
        .get(`/hr/calendar-location`)
        .then(({ data }) => data.data)

      const doctorConfigReq = httpClient
        .get(`/core/config/configCode/require_hn_for_doctor_appointment`)
        .then(({ data }) => data.data)

      const roomConfigReq = httpClient
        .get(
          `/core/config/configCode/enable_whole_service_room_view_appointment`,
        )
        .then(({ data }) => data.data)

      const [
        service,
        specialty,
        doctors,
        location,
        doctorConfig,
        roomConfig,
      ] = await Promise.all([
        serviceReq,
        specialtyReq,
        doctorReq,
        locationReq,
        doctorConfigReq,
        roomConfigReq,
      ])

      let option = specialty?.map((item) => ({
        ...item,
        label: `${item.specialtyCode} - ${item.name}`,
      }))

      if (request === providerRequest.current) {
        setOptions({
          service: service,
          specialty: option,
          doctors: doctors,
          location: location,
        })
        setConfig({ doctor: doctorConfig, room: roomConfig })
      }
    } catch (error) {
      if (request === providerRequest.current) setProviderError(true)
    } finally {
      if (request === providerRequest.current) setProviderLoading(false)
    }
  }

  useEffect(() => {
    getOptions()
  }, [providerRefresh])

  useEffect(() => {
    let active = true

    const loadAppointmentUiConfig = async () => {
      setIsLoadingAppointmentUiConfig(true)
      try {
        const response = await httpClient.get(APPOINTMENT_UI_CONFIG_URL, {
          cache: false,
        })
        const nextConfig = getAppointmentUiConfigFromResponse(response)

        if (active) {
          setAppointmentUiConfig(nextConfig)
          setAppointmentUiConfigDraft(nextConfig)
        }
      } catch (error) {
        if (active) {
          setAppointmentUiConfig(DEFAULT_APPOINTMENT_UI_CONFIG)
          setAppointmentUiConfigDraft(DEFAULT_APPOINTMENT_UI_CONFIG)
        }
      } finally {
        if (active) setIsLoadingAppointmentUiConfig(false)
      }
    }

    loadAppointmentUiConfig()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (isDebugMode) setShowAppointmentUiConfig(true)
  }, [isDebugMode])

  const saveAppointmentUiConfig = async () => {
    setIsSavingAppointmentUiConfig(true)
    try {
      let currentConfig = { configCode: APPOINTMENT_UI_CONFIG_CODE }

      try {
        const response = await httpClient.get(APPOINTMENT_UI_CONFIG_URL, {
          cache: false,
        })
        currentConfig = getConfigRecord(response)
      } catch (error) {}

      const currentJsonData = parseJsonObject(currentConfig?.jsonData)
      const nextAppointmentUiConfig = normalizeAppointmentUiConfig({
        ...parseJsonObject(currentJsonData[APPOINTMENT_UI_CONFIG_KEY]),
        ...appointmentUiConfigDraft,
      })
      const nextConfig = {
        ...currentConfig,
        configCode: APPOINTMENT_UI_CONFIG_CODE,
        jsonData: {
          ...currentJsonData,
          [APPOINTMENT_UI_CONFIG_KEY]: nextAppointmentUiConfig,
        },
      }

      await httpClient.put(APPOINTMENT_UI_CONFIG_URL, nextConfig)
      setAppointmentUiConfig(nextAppointmentUiConfig)
      setAppointmentUiConfigDraft(nextAppointmentUiConfig)
      setShowAppointmentUiConfig(false)
      await Swal.fire(
        'บันทึกสำเร็จ',
        'บันทึกการตั้งค่าหน้าจองนัดหมายแล้ว',
        'success',
      )
    } catch (error) {
      await Swal.fire(
        'บันทึกการตั้งค่าไม่สำเร็จ',
        error?.response?.data?.message || error?.message || 'เกิดข้อผิดพลาด',
        'error',
      )
    } finally {
      setIsSavingAppointmentUiConfig(false)
    }
  }

  const handleOpenNewTab = (path) => {
    const newTab = window.open(window.location.origin + path, '_blank')
    if (newTab) {
      newTab.opener = null
    }
  }

  return (
    <React.Fragment>
      <Row>
        <Col>
          <Card className="p-3">
            {appointmentUiConfig?.providerRole === true && (
              <div className="mb-3">
                <ProviderRoleTabs
                  roles={providerRoles}
                  value={providerRole}
                  onChange={selectProviderRole}
                />
              </div>
            )}
            {providerError && <div role="alert" className="text-danger mb-2">โหลดข้อมูลผู้ให้บริการไม่สำเร็จ <Button variant="link" onClick={() => setProviderRefresh((value) => value + 1)}>ลองใหม่</Button></div>}
            <Card.Title>ค้นหาแพทย์หรือแผนก</Card.Title>
            <Card.Body className="calendar">
              <Row className="align-items-end gy-2">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>{providerRole === 'DOCTOR' ? 'แพทย์' : 'ผู้ให้บริการ'}</Form.Label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      placeholder="ทั้งหมด"
                      name="doctor"
                      value={filter?.doctor}
                      options={options.doctors || []}
                      getOptionLabel={(option) => option.fullName}
                      getOptionValue={(option) => option.employeeCode}
                      formatOptionLabel={showProviderColor
                        ? (option) => (
                          <span className="d-flex align-items-center">
                            <span
                              aria-hidden="true"
                              className="mr-2"
                              style={{
                                width: 14,
                                height: 14,
                                flexShrink: 0,
                                borderRadius: 3,
                                backgroundColor: getProviderHexColor(option, providerRoles, providerRole) || '#A5A5A5',
                              }}
                            />
                            <span>{option.fullName}</span>
                          </span>
                        )
                        : undefined}
                      onChange={(e) => handleChange('doctor', e)}
                      isClearable
                      isDisabled={providerLoading || providerError}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>แผนก</Form.Label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      placeholder="ทั้งหมด"
                      name="specialty"
                      value={filter?.specialty}
                      options={options.specialty || []}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.specialtyCode}
                      onChange={(e) => handleChange('specialty', e)}
                      isClearable
                    />
                  </Form.Group>
                </Col>

                <Col
                  xs={12}
                  md={4}
                  className="d-flex justify-content-md-end justify-content-center my-auto"
                >
                  <div className="mt-md-0 mt-2">
                    <Button
                      variant="outline-primary"
                      className="me-2"
                      onClick={() =>
                        handleOpenNewTab(`/his#/settings/doctor-calendar`)
                      }
                    >
                      {showProviderColor && <FiCalendar className="mr-2" />}
                      จัดการตารางออกเวร
                    </Button>
                  </div>
                </Col>
              </Row>
              {appointmentUiConfig?.providerRole === true && !providerLoading && !providerError && providerRole !== 'DOCTOR' && options.doctors.length === 0 && (
                <div className="text-muted mt-2">ไม่พบผู้ให้บริการประเภทนี้</div>
              )}

              <Row>
                <Col sm="12" className="my-3">
                  <AppointmentCalendarColorContext.Provider value={showProviderColor}>
                    <ExaminationSchedule
                      key={providerRole}
                      providerRole={providerRole}
                      providerRoles={providerRoles}
                      providerLoading={providerLoading || providerError}
                      stateParent={{
                        filtered: [filter, setFilter],
                        config: [config, setConfig],
                        configId: [calendarConfigTypeId, setCalendarConfigTypeId],
                      }}
                      consultationOption={consultationOption}
                      doctorOption={options.doctors}
                      options={options}
                      appointmentUiConfig={appointmentUiConfig}
                      designerData={designerData}
                      designerKey={designerKey}
                      hn={appointmentHn}
                      scheduleViewMode={
                        scheduleViewModeFromPath ||
                        (data?.scheduleViewMode ??
                          designerData?.scheduleViewMode ??
                          'week')
                      }
                    />
                  </AppointmentCalendarColorContext.Provider>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <AppointmentUiConfigModal
        config={appointmentUiConfigDraft}
        loading={isLoadingAppointmentUiConfig}
        onConfigChange={setAppointmentUiConfigDraft}
        onHide={() => setShowAppointmentUiConfig(false)}
        onSave={saveAppointmentUiConfig}
        saving={isSavingAppointmentUiConfig}
        show={showAppointmentUiConfig}
      />
    </React.Fragment>
  )
}

Calendar.defaultProps = {
  designerData: {
    scheduleViewMode: 'week',
    hn: '',
  },
  hn: '',
}

export default Calendar
